import { concat } from '@ember/helper';
import { t } from 'ember-intl';
import { cached, tracked } from '@glimmer/tracking';
import { trackedObject } from '@ember/reactive/collections';
import Component from '@glimmer/component';
import didIntersect from 'ember-scroll-modifiers/modifiers/did-intersect';
import safeFormatDate from '../../helpers/safe-format-date.ts';
import safeFormatTime from '../../helpers/safe-format-time.ts';
import Button from 'tio-ui/components/button';
import Chip from 'tio-ui/components/chip';
import SnapshotPreview from './snapshot-preview.gts';
import Table from 'tio-ui/components/table';
import Paginator from 'tio-ui/components/paginator';
import type { EmployeeExtension as EmployeeModel } from 'tio-data/data/resources/employee.ext';
import type { NotificationHistoryExtension as NotificationHistoryModel } from 'tio-data/data/resources/notification-history.ext';
import { Request } from 'tio-data';
import { notificationHistoriesRequest } from 'tio-data/requests/profile-history';

interface NotificationHistoriesTableSignature {
  Args: {
    employee: EmployeeModel;
    noteSubtypeFilter: string;
    /** Maximum records per request; changing it resets pagination. */
    limit: number;
  };
}

export default class NotificationHistoriesTableComponent extends Component<NotificationHistoriesTableSignature> {
  @tracked isInViewport = false;
  @tracked snapshotRow: NotificationHistoryModel | null = null;

  /** Starts a fresh page whenever the employee, subtype or page size changes. */
  @cached
  get pagination() {
    return trackedObject({
      employeeId: this.args.employee?.id,
      filter: this.args.noteSubtypeFilter,
      limit: this.limit,
      page: 1,
    });
  }

  get page() {
    return this.pagination.page;
  }

  get limit() {
    return this.args.limit || 10;
  }

  openSnapshot = (row: NotificationHistoryModel) => {
    this.snapshotRow = row;
  };

  closeSnapshot = () => {
    this.snapshotRow = null;
  };

  changePaginatorPage = (page: number) => {
    this.pagination.page = page;
  };

  <template>
    <span {{didIntersect onEnter=(fn (mut this.isInViewport) true)}} />
    {{#if this.isInViewport}}
      {{#if @employee.id}}
        <Request
          @query={{notificationHistoriesRequest
            @employee.id
            this.page
            this.limit
            @noteSubtypeFilter
          }}
        >
          <:error as |_error features|>
            <div role="alert" class="tio-col-tight" data-test-notification-histories-error>
              <p class="tio-fg-danger">{{t "common.records.load_error"}}</p>
              <Button
                @appearance="outlined"
                @testHook="notification-histories__retry"
                data-test-notification-histories-retry
                {{on "click" features.retry}}
              >{{t "common.records.retry"}}</Button>
            </div>
          </:error>
          <:always as |state|>
            {{#unless state.isError}}
              <Table @isLoading={{state.isPending}}>
                <:thead as |options|>
                  <options.tr>
                    <options.th class="w-48">{{t
                        "common.profile.notification_histories.sent_on"
                      }}</options.th>
                    <options.th>{{t
                        "common.profile.notification_histories.email_type"
                      }}</options.th>
                    <options.th>{{t "common.profile.notification_histories.sent_to"}}</options.th>
                    <options.th>{{t "common.profile.notification_histories.built_by"}}</options.th>
                    <options.th class="w-20"><span class="sr-only">{{t
                          "common.profile.notification_histories.view_sent"
                        }}</span></options.th>
                  </options.tr>
                </:thead>
                <:tbody as |options|>
                  {{#each state.result.data as |history|}}
                    <options.tr data-test-notification-history-row={{history.id}}>
                      <options.td>
                        {{safeFormatDate
                          history.createdAt
                          month="short"
                          day="2-digit"
                          year="numeric"
                        }}
                        {{safeFormatTime
                          history.createdAt
                          hour="numeric"
                          minute="numeric"
                          second="numeric"
                        }}
                      </options.td>
                      <options.td>{{history.noteSubtype}}</options.td>
                      <options.td>{{history.toEmail}}</options.td>
                      <options.td data-test-built-by={{history.id}}>
                        {{! hasSentSnapshot is the provenance signal: the snapshot is
                    only written when tio-api rendered the delivered content. }}
                        {{#if history.hasSentSnapshot}}
                          <Chip @size="sm" @intent="success">{{t
                              "common.profile.notification_histories.built_by_tio"
                            }}</Chip>
                        {{else}}
                          <Chip @size="sm">{{t
                              "common.profile.notification_histories.built_by_sendgrid"
                            }}</Chip>
                        {{/if}}
                        {{#if history.renderingMode}}
                          {{! Translate the rendering-mode enum, mirroring mode-chip's
                      shared labels — a raw value would show English to
                      es-mx/fr-ca admins. }}
                          <span
                            class="tio-fg-muted text-xs ml-1"
                            data-test-rendering-mode={{history.id}}
                          >
                            {{t
                              (concat
                                "common.profile.notification_histories.mode_" history.renderingMode
                              )
                            }}
                          </span>
                        {{/if}}
                      </options.td>
                      <options.td>
                        <Button
                          @appearance="minimal"
                          @size="sm"
                          data-test-view-sent={{history.id}}
                          {{on "click" (fn this.openSnapshot history)}}
                        >{{t "common.profile.notification_histories.view_sent"}}</Button>
                      </options.td>
                    </options.tr>
                  {{else}}
                    <options.tr>
                      <options.td colspan="5">
                        {{t "common.profile.notification_histories.no_communications"}}
                      </options.td>
                    </options.tr>
                  {{/each}}
                </:tbody>
                <:tfoot>
                  <tr>
                    <td class="text-right" colspan="9">
                      {{#if (gt (or state.result.meta.page.total 0) this.limit)}}
                        <Paginator
                          @simple={{true}}
                          @currentPage={{this.page}}
                          @totalItems={{or state.result.meta.page.total 0}}
                          @pageSize={{this.limit}}
                          @onChange={{this.changePaginatorPage}}
                        />
                      {{/if}}
                    </td>
                  </tr>
                </:tfoot>
              </Table>
            {{/unless}}
          </:always>
        </Request>
      {{/if}}

      {{#if this.snapshotRow}}
        {{#each (array this.snapshotRow) key="id" as |row|}}
          <SnapshotPreview @row={{row}} @onClose={{this.closeSnapshot}} />
        {{/each}}
      {{/if}}
    {{/if}}
  </template>
}
